import { useState, type FormEvent } from 'react';
import { PasswordSignInSchema, PasswordSignUpSchema } from '@kaeliux/shared';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

type Field = 'email' | 'password' | 'confirmPassword';

export interface PasswordAuthFormProps {
    mode: 'signIn' | 'signUp';
    submitLabel: string;
    /** Rejections are expected to be surfaced by the caller (store error, local message). */
    onSubmit: (email: string, password: string) => Promise<void>;
}

export default function PasswordAuthForm({ mode, submitLabel, onSubmit }: PasswordAuthFormProps) {
    const [values, setValues] = useState<Record<Field, string>>({ email: '', password: '', confirmPassword: '' });
    const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
    const [submitting, setSubmitting] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const isSignUp = mode === 'signUp';
    const passwordType = passwordVisible ? 'text' : 'password';

    const update = (field: Field) => (event: { target: { value: string } }) =>
        setValues((current) => ({ ...current, [field]: event.target.value }));

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const parsed = isSignUp ? PasswordSignUpSchema.safeParse(values) : PasswordSignInSchema.safeParse(values);
        if (!parsed.success) {
            const fieldErrors: Partial<Record<Field, string>> = {};
            for (const issue of parsed.error.issues) {
                const field = issue.path[0] as Field;
                fieldErrors[field] ??= issue.message;
            }
            setErrors(fieldErrors);
            return;
        }

        setErrors({});
        setSubmitting(true);
        try {
            await onSubmit(parsed.data.email, parsed.data.password);
        } catch {
            // See onSubmit's doc comment.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-3">
            <Input
                label="Email"
                type="email"
                autoComplete="email"
                value={values.email}
                onChange={update('email')}
                error={errors.email}
            />
            <Input
                label="Password"
                type={passwordType}
                trailingSlot={
                    <button
                        type="button"
                        aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                        aria-pressed={passwordVisible}
                        onClick={() => setPasswordVisible((visible) => !visible)}
                        className="text-label font-semibold text-content-muted hover:text-content-primary focus-visible:outline-none focus-visible:underline"
                    >
                        {passwordVisible ? 'Hide' : 'Show'}
                    </button>
                }
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                value={values.password}
                onChange={update('password')}
                error={errors.password}
                hint={isSignUp ? 'At least 8 characters' : undefined}
            />
            {isSignUp && (
                <Input
                    label="Confirm password"
                    type={passwordType}
                    autoComplete="new-password"
                    value={values.confirmPassword}
                    onChange={update('confirmPassword')}
                    error={errors.confirmPassword}
                />
            )}
            <Button type="submit" size="lg" fullWidth loading={submitting} className="mt-1">
                {submitLabel}
            </Button>
        </form>
    );
}
